import { FunctionComponent, ReactNode, useEffect, useRef } from 'react'
import { CuiBottomSheet } from 'front-lib'
import { useIsMobile } from 'src/hooks/useIsMobile'
import './TooltipOrBottomSheet.scss'

interface TooltipOrBottomSheetProps {
  isOpen: boolean
  children: ReactNode
  onClose?: () => void
  parentRef?: React.RefObject<HTMLElement>
}

export const TooltipOrBottomSheet: FunctionComponent<TooltipOrBottomSheetProps> = ({
  isOpen,
  children,
  onClose,
  parentRef
}) => {
  const isMobile = useIsMobile()
  const tooltipRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(event.target as Node) &&
        onClose
      ) {
        onClose()
      }
    }

    if (isOpen && !isMobile) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen, isMobile, onClose])

  useEffect(() => {
    if (isOpen && !isMobile && tooltipRef.current && parentRef?.current) {
      const parentRect = parentRef.current.getBoundingClientRect()

      // Position the tooltip below the parent element
      tooltipRef.current.style.top = `${parentRect.bottom + 8}px`
      tooltipRef.current.style.left = `${parentRect.left +
        parentRect.width / 2}px`
    }
  }, [isOpen, isMobile, parentRef])

  if (!isOpen) return null

  if (isMobile) {
    return (
      <CuiBottomSheet isOpen={isOpen}>
        <CuiBottomSheet.Header onRequestClose={onClose} />
        <CuiBottomSheet.Body>{children}</CuiBottomSheet.Body>
      </CuiBottomSheet>
    )
  }

  return (
    <div
      ref={tooltipRef}
      className="core-ux-tooltip-or-bottom-sheet-desktop"
      data-testid="tooltip-desktop">
      <div className="tooltip-arrow" />
      {children}
    </div>
  )
}
